/**
 * ThemeContext — Priorities.ai theme (light / dark)
 *
 * Manages dark / light mode for the whole app.
 * - Default: dark
 * - Persists preference to:
 *     localStorage (key "pp-theme") — for fast, no-flash initial load
 *     uiStore.setTheme()            — for Supabase sync per user account
 * - Syncs both DOM signals simultaneously:
 *     `data-theme` attribute on <html>  → drives CSS custom property overrides
 *     `.dark` class on <html>           → drives Tailwind dark: variants
 *
 * SOURCE OF TRUTH: ThemeContext (localStorage). uiStore mirrors it.
 * Supabase preferences may seed the theme only when localStorage has no
 * explicit "pp-theme" yet — they must never overwrite a user choice already
 * stored locally (that race was reverting light mode after toggle).
 *
 * Elevation override: call setForcedLight(true) to pin the DOM to light mode
 * without changing the stored preference. Call setForcedLight(false) to
 * release the override and restore the user's saved theme.
 *
 * Light palette: there is one — warm paper, shared with the public site. The
 * `.light-daylight` class is still set on <html> in light mode because the
 * accent remaps in index.css are keyed on it. The cool "classic" ramp and its
 * ?ramp= switch were retired 2026-10-01 (owner ruling).
 */

import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { useUIStore } from '../stores/prioritization/uiStore';

type Theme = 'dark' | 'light';

interface ThemeContextValue {
  theme: Theme;
  isDark: boolean;
  toggleTheme: () => void;
  setTheme: (t: Theme) => void;
  /** Pin DOM to light mode without persisting the change. */
  setForcedLight: (forced: boolean) => void;
}

const ThemeContext = createContext<ThemeContextValue>({
  theme: 'dark',
  isDark: true,
  toggleTheme: () => {},
  setTheme: () => {},
  setForcedLight: () => {},
});

function readStoredTheme(): Theme | null {
  try {
    const saved = localStorage.getItem('pp-theme');
    if (saved === 'light' || saved === 'dark') return saved;
  } catch {
    // localStorage may be unavailable (private mode / SSR)
  }
  return null;
}

function getInitialTheme(): Theme {
  return readStoredTheme() ?? 'dark';
}

function writeStoredTheme(t: Theme) {
  try {
    localStorage.setItem('pp-theme', t);
  } catch {
    // localStorage may be unavailable (private mode / SSR)
  }
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(getInitialTheme);
  const setUITheme = useUIStore((s) => s.setTheme);
  // Prevents circular update when we set uiStore and it fires a subscription
  const ownUpdate = useRef(false);
  // When true, DOM is pinned to light regardless of `theme` state.
  const forcedLightRef = useRef(false);

  function applyThemeToDom(t: Theme) {
    const effective: Theme = forcedLightRef.current ? 'light' : t;
    const html = document.documentElement;
    html.setAttribute('data-theme', effective);
    if (effective === 'dark') {
      html.classList.add('dark');
    } else {
      html.classList.remove('dark');
    }
    html.classList.add('light-daylight');
  }

  // Apply DOM changes and enable smooth transitions after first paint
  useEffect(() => {
    applyThemeToDom(theme);
    const raf = requestAnimationFrame(() => {
      document.documentElement.classList.add('theme-transitions');
    });
    return () => cancelAnimationFrame(raf);
  }, [theme]);

  // Seed from Supabase preferences only when the user has never chosen a theme
  // in this browser. Once "pp-theme" exists, localStorage wins — otherwise a
  // stale DB value (or a fetch that races a toggle) snaps the UI back to dark.
  useEffect(() => {
    return useUIStore.subscribe((state) => {
      if (ownUpdate.current) return;
      if (readStoredTheme() !== null) return;
      const stored = state.theme;
      if (stored === 'light' || stored === 'dark') {
        writeStoredTheme(stored);
        setThemeState(stored);
      }
    });
  }, []);

  function setTheme(t: Theme) {
    // Persist locally first so any concurrent uiStore subscription cannot
    // treat this browser as "unset" and re-apply a stale DB theme.
    writeStoredTheme(t);
    setThemeState(t);
    ownUpdate.current = true;
    setUITheme(t);
    // Keep the guard through the save microtask / next frames — rAF alone was
    // too short when fetchPreferencesFromSupabase resolved in the same tick.
    queueMicrotask(() => {
      requestAnimationFrame(() => {
        ownUpdate.current = false;
      });
    });
  }

  function toggleTheme() {
    setTheme(theme === 'dark' ? 'light' : 'dark');
  }

  function setForcedLight(forced: boolean) {
    forcedLightRef.current = forced;
    applyThemeToDom(theme);
  }

  return (
    <ThemeContext.Provider value={{ theme, isDark: theme === 'dark', toggleTheme, setTheme, setForcedLight }}>
      {children}
    </ThemeContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components -- hook paired with provider
export function useTheme() {
  return useContext(ThemeContext);
}
